import React, { useEffect, useMemo, useState } from "react";

import { CircularProgress, Grid, Tab, Tabs } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { getPickerShiftReport } from "api/shipment";

import {
  DatePickerGroup,
  Dropdown,
  InputContainer,
  TextInput,
} from "components/Form";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";
import Widget from "components/Widget";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

import { Timer, User } from "icons";

import { convertDataToTableView } from "utils/convert";
import { convertDate } from "utils/date";
import { exportToExcel } from "utils/exportToExcel";
import { toTitleCase } from "utils/string";

const TABS = ["picked", "transferred", "times"];
const TAB_FILE_NAMES = ["Pieces-Picked", "Pieces-Transferred", "Start-Times"];

const lastWeek = new Date();
lastWeek.setDate(lastWeek.getDate() - 6);

const formatNumber = (value) => (value ? value.toLocaleString() : "-");

export default function PickerReport() {
  const layoutDispatch = useLayoutDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const [employee, setEmployee] = useState("");
  const [exportDisabled, setExportDisabled] = useState(false);
  const [report, setReport] = useState({
    shifts: [],
    classes: [],
    pieces: [],
    days: [],
    gap_minutes: 20,
    isFetching: true,
  });
  const [params, setParams] = useState({
    start_date: convertDate(lastWeek),
    end_date: convertDate(new Date()),
    shift: "",
  });

  useEffect(() => {
    if (report.isFetching) {
      getPickerShiftReport({
        params,
        responseSetter: (response) => {
          if (!response || response.success === false) {
            setReport((prev) => ({
              ...prev,
              pieces: [],
              days: [],
              isFetching: false,
            }));
            return popupNotification({
              dispatch: layoutDispatch,
              message: response?.message || "Server Error",
            });
          }
          setReport({ ...response, isFetching: false });
        },
      });
    }
  }, [params, layoutDispatch, report.isFetching]);

  const handleChangeFilterInput = (prop) => (event) => {
    event.persist();
    setParams((prev) => ({ ...prev, [prop]: event.target.value }));
    setReport((prev) => ({ ...prev, isFetching: true }));
  };

  const matchesEmployee = (row) =>
    row.username.toLowerCase().includes(employee.trim().toLowerCase());

  const operation = TABS[activeTab];

  const pieceRows = useMemo(
    () =>
      report.pieces.filter(
        (row) => row[`${operation}_total`] > 0 && matchesEmployee(row),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report.pieces, operation, employee],
  );

  const dayRows = useMemo(
    () => report.days.filter(matchesEmployee),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report.days, employee],
  );

  // Every render takes the table's `exported` flag: the export gets plain values (numbers stay
  // numbers in Excel), the screen gets the formatted ones.
  const employeeHeaders = [
    {
      id: "username",
      label: "Employee",
      render: (r, exported) =>
        exported ? (
          toTitleCase(r.username)
        ) : (
          <strong>{toTitleCase(r.username)}</strong>
        ),
    },
    { id: "shift", label: "Shift", render: (r) => r.shift },
  ];

  const numberCell = (value, exported) =>
    exported ? value || 0 : formatNumber(value);

  const pieceHeaders = [
    ...employeeHeaders,
    ...report.classes
      .filter((item) => pieceRows.some((row) => row[operation][item.value]))
      .map((item) => ({
        id: item.value,
        label: item.sizes ? `${item.title} (${item.sizes})` : item.title,
        exportLabel: item.sizes ? `${item.title} (${item.sizes})` : item.title,
        render: (r, exported) => numberCell(r[operation][item.value], exported),
      })),
    {
      id: "total",
      label: "Total",
      render: (r, exported) =>
        exported ? (
          r[`${operation}_total`] || 0
        ) : (
          <strong>{formatNumber(r[`${operation}_total`])}</strong>
        ),
    },
  ];

  const dayHeaders = [
    { id: "day", label: "Day", render: (r) => r.day },
    ...employeeHeaders,
    { id: "start", label: "Start", render: (r) => r.start },
    { id: "end", label: "End", render: (r) => r.end },
    {
      id: "picked",
      label: "Picked",
      render: (r, exported) => numberCell(r.picked, exported),
    },
    {
      id: "transferred",
      label: "Transferred",
      render: (r, exported) => numberCell(r.transferred, exported),
    },
    {
      id: "gaps",
      label: `Gaps (${report.gap_minutes}+ min)`,
      render: (r) =>
        r.gaps.length
          ? r.gaps
              .map((gap) => `${gap.start} - ${gap.end} (${gap.minutes} min)`)
              .join(", ")
          : "-",
    },
  ];

  const rows = operation === "times" ? dayRows : pieceRows;
  const headers = operation === "times" ? dayHeaders : pieceHeaders;

  // Exports what the active tab shows, employee filter included.
  const handleExportData = () =>
    exportToExcel(
      [],
      `Picker-Report-${TAB_FILE_NAMES[activeTab]}-${params.start_date}_${params.end_date}`,
      true,
      convertDataToTableView(rows, headers),
      setExportDisabled,
    );

  return (
    <>
      <PageTitle title="Picker Report" />
      <InputContainer style={{ marginTop: 15 }}>
        <Grid container>
          <DatePickerGroup
            startDateValue={params.start_date}
            startDateOnChange={handleChangeFilterInput("start_date")}
            endDateValue={params.end_date}
            endDateOnChange={handleChangeFilterInput("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2 }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="shift"
              title="Shift"
              value={params.shift}
              onChange={handleChangeFilterInput("shift")}
              items={report.shifts}
              LeftIcon={Timer}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              fullWidth
              label="Employee"
              variant="outlined"
              value={employee}
              onChange={(e) => setEmployee(e.target.value)}
              LeftIcon={User}
            />
          </Grid>
        </Grid>
      </InputContainer>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
            >
              <Tab label="Pieces Picked" />
              <Tab label="Pieces Transferred" />
              <Tab label="Start Times & Gaps" />
            </Tabs>
            {report.isFetching ? (
              <CircularProgress />
            ) : !rows.length ? (
              <Alert severity="info">No results found!</Alert>
            ) : (
              <DefaultTable
                headers={headers}
                data={{ items: rows, isFetching: false }}
                handleExportData={handleExportData}
                exportDisabled={exportDisabled}
                setExportDisabled={setExportDisabled}
              />
            )}
          </Widget>
        </Grid>
      </Grid>
    </>
  );
}
